import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Dialog,
  Field,
  Select,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type {
  ComplianceFramework,
  EvidenceCollection,
  EvidenceExportFormat,
  EvidencePackage,
  EvidencePackageExport,
  FrameworkVersion,
} from "@odingard/sdk-client";
import {
  EVIDENCE_EXPORT_FORMATS,
  EVIDENCE_UNSUPPORTED_EXPORT_FORMATS,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { evidenceApi, frameworksApi } from "../../lib/api";
import {
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  useGovernedMutation,
} from "../../governance";

const FORMAT_LABEL: Record<EvidenceExportFormat, string> = {
  csv: "CSV",
  manifest_json: "Manifest JSON",
};

// Evidence packages (APP-004 R3 P2): a frozen, hash-anchored set of approved
// evidence versions, plus the record of every export taken from it.
export function GovEvidencePackages() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [selected, setSelected] = useState<string | null>(null);

  const packages = useAsync(async () => (await evidenceApi.packages(orgId)).data.packages, [orgId]);
  const collections = useAsync(
    async () => (await evidenceApi.collections(orgId)).data.collections,
    [orgId],
  );
  const versions = useAsync<{ version: FrameworkVersion; framework: ComplianceFramework }[]>(
    async () => {
      const frameworks = (await frameworksApi.frameworks(orgId, { scope: "tenant" })).data.frameworks;
      return (
        await Promise.all(
          frameworks.map(async (framework) =>
            (await frameworksApi.versions(orgId, framework.id)).data.versions.map((version) => ({
              version,
              framework,
            })),
          ),
        )
      ).flat();
    },
    [orgId],
  );

  const manifest = useAsync(
    async () => (selected ? (await evidenceApi.manifest(orgId, selected)).data.manifest : null),
    [orgId, selected],
  );
  const exports = useAsync(
    async () => (selected ? (await evidenceApi.exports(orgId, selected)).data.exports : []),
    [orgId, selected],
  );

  const collectionName = (id: string | null) =>
    id === null ? "All collections" : (collections.data?.find((c) => c.id === id)?.name ?? id);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Evidence packages</h1>
          <p>
            A package freezes the approved, evidence inside its window versions in scope. The manifest hash covers the
            ordered entries, so regenerating an unchanged package produces the same hash.{" "}
            <Link to="/studio/governance/evidence">Back to evidence</Link>
          </p>
        </div>
        <div className="badge-row">
          <CreatePackage
            orgId={orgId}
            collections={collections.data ?? []}
            versions={versions.data ?? []}
            onDone={packages.reload}
          />
        </div>
      </div>

      <GovPanel
        title="Packages"
        state={packages}
        loadingLabel="Loading packages…"
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="No packages yet"
        emptyDescription="Create a package once the evidence an auditor asked for has been approved."
      >
        {(rows) => (
          <DataTable
            caption="Evidence packages"
            onRowClick={(row) => setSelected(row.id)}
            columns={[
              {
                key: "name",
                header: "Package",
                render: (row) => (
                  <>
                    <strong>{row.name}</strong>
                    <div className="muted-note">{collectionName(row.collectionId)}</div>
                  </>
                ),
              },
              { key: "items", header: "Items", render: (row) => row.itemCount },
              {
                key: "hash",
                header: "Manifest hash",
                render: (row) => <code>{row.manifestHash.slice(0, 16)}…</code>,
              },
              { key: "generated", header: "Frozen", render: (row) => formatDate(row.generatedAt) },
              {
                key: "actions",
                header: "",
                render: (row) => (
                  <Button variant="ghost" size="sm" onClick={() => setSelected(row.id)}>
                    Manifest
                  </Button>
                ),
              },
            ]}
            rows={rows as (EvidencePackage & { id: string })[]}
          />
        )}
      </GovPanel>

      {selected ? (
        <>
          <GovPanel
            title="Frozen manifest"
            state={manifest}
            loadingLabel="Loading manifest…"
            action={
              <div className="badge-row">
                {EVIDENCE_EXPORT_FORMATS.map((format) => (
                  <GovernedAction
                    key={format}
                    permission={GOVERNANCE_PERMISSIONS.reportsExport}
                    label={`Export ${FORMAT_LABEL[format]}`}
                    title={`Export ${FORMAT_LABEL[format]}`}
                    description="Renders the frozen manifest. The package itself does not change."
                    consequence="Every export is recorded with its own content hash, so a copy shared with an auditor can be traced back."
                    confirmLabel="Export"
                    busyLabel="Exporting…"
                    onConfirm={() => evidenceApi.exportPackage(orgId, selected, format)}
                    onDone={exports.reload}
                  />
                ))}
                {EVIDENCE_UNSUPPORTED_EXPORT_FORMATS.map((format) => (
                  <Button key={format} variant="ghost" size="sm" disabled>
                    {format.toUpperCase()} unavailable
                  </Button>
                ))}
              </div>
            }
          >
            {(data) =>
              data === null ? (
                <p className="muted-note">Select a package.</p>
              ) : (
                <>
                  <dl className="detail-grid">
                    <div>
                      <dt>Manifest version</dt>
                      <dd>{data.manifestVersion}</dd>
                    </div>
                    <div>
                      <dt>Items</dt>
                      <dd>{data.itemCount}</dd>
                    </div>
                    <div>
                      <dt>Manifest hash</dt>
                      <dd>
                        <code>{data.manifestHash}</code>
                      </dd>
                    </div>
                    <div>
                      <dt>Scope</dt>
                      <dd>{collectionName(data.collectionId)}</dd>
                    </div>
                  </dl>
                  <DataTable
                    caption="Manifest entries"
                    columns={[
                      { key: "position", header: "#", render: (row) => row.position },
                      {
                        key: "title",
                        header: "Evidence",
                        render: (row) => (
                          <Link to={`/studio/governance/evidence/items/${row.evidenceItemId}`}>
                            {row.title}
                          </Link>
                        ),
                      },
                      {
                        key: "version",
                        header: "Version",
                        render: (row) => <Badge tone="info">v{row.versionNumber}</Badge>,
                      },
                      {
                        key: "hash",
                        header: "Content hash",
                        render: (row) => <code>{row.contentHash}</code>,
                      },
                    ]}
                    rows={data.entries.map((entry) => ({ ...entry, id: entry.evidenceVersionId }))}
                  />
                  <p className="muted-note">
                    XLSX and PDF need the Reporting platform&apos;s renderer and are unavailable from Policy AI, so
                    CSV and manifest JSON are reproducible byte for byte.
                  </p>
                </>
              )
            }
          </GovPanel>

          <Card>
            <CardHeader title="Export history" />
            <CardBody>
              <GovPanel
                bare
                state={exports}
                loadingLabel="Loading export history…"
                isEmpty={(rows) => rows.length === 0}
                emptyTitle="No exports yet"
                emptyDescription="Exports are recorded here with their own content hash."
              >
                {(rows) => (
                  <DataTable
                    caption="Package export history"
                    columns={[
                      {
                        key: "format",
                        header: "Format",
                        render: (row) => <Badge>{FORMAT_LABEL[row.format] ?? row.format}</Badge>,
                      },
                      {
                        key: "manifest",
                        header: "Manifest hash",
                        render: (row) => <code>{row.manifestHash.slice(0, 16)}…</code>,
                      },
                      {
                        key: "content",
                        header: "Content hash",
                        render: (row) => <code>{row.contentHash.slice(0, 16)}…</code>,
                      },
                      { key: "size", header: "Bytes", render: (row) => row.byteSize },
                      { key: "at", header: "Exported", render: (row) => formatDate(row.createdAt) },
                    ]}
                    rows={rows as (EvidencePackageExport & { id: string })[]}
                  />
                )}
              </GovPanel>
            </CardBody>
          </Card>
        </>
      ) : null}
    </>
  );
}

function CreatePackage({
  orgId,
  collections,
  versions,
  onDone,
}: {
  orgId: string;
  collections: EvidenceCollection[];
  versions: { version: FrameworkVersion; framework: ComplianceFramework }[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [frameworkVersionId, setFrameworkVersionId] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.evidenceManage);

  const submit = async () => {
    const ok = await mutation.run(() =>
      evidenceApi.createPackage(orgId, {
        name: name.trim(),
        collectionId: collectionId || null,
        frameworkVersionId: frameworkVersionId || null,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setName("");
    onDone();
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        New package
      </Button>
      <Dialog
        open={open}
        title="New evidence package"
        description="Freezes the approved, evidence inside its window versions in scope."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={name.trim().length === 0 || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Freezing…" : "Create package"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Name">
          {(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Collection" help="Leave unscoped to include every approved item.">
          {(id) => (
            <Select id={id} value={collectionId} onChange={(e) => setCollectionId(e.target.value)}>
              <option value="">All collections</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Framework version">
          {(id) => (
            <Select
              id={id}
              value={frameworkVersionId}
              onChange={(e) => setFrameworkVersionId(e.target.value)}
            >
              <option value="">Not scoped to a framework</option>
              {versions.map(({ version, framework }) => (
                <option key={version.id} value={version.id}>
                  {framework.name} {version.versionLabel}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </Dialog>
    </>
  );
}
